import Vue from 'vue';
import FloatingPanel from 'components/widgets/FloatingPanel/FloatingPanel.vue';
import UpDown from 'components/widgets/UpDown/UpDown.vue';
import { scaleLinear } from 'd3';
import FormatUtils from 'src/utils/formatters';
import InteractionMenu from 'src/components/widgets/InteractionMenu.vue';
export default Vue.extend({
    components: { FloatingPanel, UpDown, InteractionMenu },
    props: {
        /** dataset to show: array of objects */
        rows: { type: Array, required: true, default: () => [] },
        width: { type: Number, required: false, default: 0 },
        max: { type: Number, required: false, default: 0 },
        min: { type: Number, required: false, default: 0 },
        /** Array of strings for column headings*/
        headings: { type: Array, required: true, default: () => [] },
        /** Array of upto two strings for legend labels */
        legend: { type: Array, required: true, default: () => [] },
        /** what to show if an item has an empty label */
        emptySegmentText: { type: String, required: false, default: '(No Value)' },
        /** should the vertical indicator bar be shown */
        showIndicatorBar: { type: Boolean, required: false, default: false },
        hoverRowPointer: { type: Boolean, required: false, default: true },
        lineHeight: { type: Number, required: false, default: 6 },
        seriesLabels: { type: Object, required: false, default: () => ({}) }
    },
    data() {
        return {
            showRowToolTip: false,
            showLegendToolTip: false,
            rowHeight: 48,
            markerHeight: 14,
            markerWidth: 2,
            headerTextHeight: 16,
            legendHeight: 40,
            item: null,
            hoverX: 0,
            hoverY: 0,
            interactionX: 0,
            interactionY: 0,
        };
    },
    computed: {
        colWidth() {
            return this.width / (this.headings?.length ?? 1);
        },
        chartHeight() {
            return this.rows.reduce((sum, row) => sum + this.heightForRow(row), 0) + this.headerHeight + this.legendHeight;
        },
        xScale() {
            return scaleLinear()
                .domain([this.min, this.max])
                .range([0, this.width]);
        },
        showLegend() {
            return this.legend.length > 0;
        },
        headerHeight() {
            return this.headerTextHeight;
        },
        headerYPosition() {
            return this.headerHeight + (this.legendHeight * (this.legend.length === 0 ? 0 : 1));
        },
        rowsYPosition() {
            return this.headerYPosition + this.headerTextHeight * 2.5;
        },
        sortedColumnIndex() {
            return this.headings.findIndex((heading) => {
                return heading.sortable && (heading.sortAsc === true || heading.sortAsc === false);
            });
        },
        rowEls() {
            return this.rows.map((_, index) => () => this.$refs[`row_${index}`]?.[0]);
        },
    },
    methods: {
        truncate: FormatUtils.truncate,
        noValue: FormatUtils.noValue,
        rowWidth(x) {
            const start = this.xScale(x);
            const zero = this.xScale(0);
            return `${Math.abs(start - zero).toFixed(2)}px`;
        },
        updateCoordinates(event) {
            this.hoverX = event.clientX;
            this.hoverY = event.clientY;
        },
        /** when you click on a heading */
        clickedOnHeading(index) {
            this.$emit('clicked-heading', index);
        },
        /** when hovered on legend */
        legendHover(event, index = null) {
            if (event && event.type)
                switch (event.type) {
                    case 'mouseleave':
                        this.showLegendToolTip = false;
                        break;
                    case 'mouseenter':
                        this.showLegendToolTip = true;
                        this.updateCoordinates(event);
                        this.$emit('hover-legend', index);
                        break;
                }
        },
        /** when you hover on a row */
        toolTip(event, index = null) {
            if (event && event.type)
                switch (event.type) {
                    case 'mouseleave':
                        this.showRowToolTip = false;
                        this.item = null;
                        break;
                    case 'mouseenter':
                        this.showRowToolTip = true;
                        this.updateCoordinates(event);
                        this.item = index;
                        this.$emit('hover-row', index);
                        break;
                }
        },
        /** when you click on a row */
        clickedOnRow(index) {
            this.showLegendToolTip = false;
            this.showRowToolTip = false;
            this.$emit('row-clicked', index);
        },
        heightForRow(row) {
            return this.rowHeight + (((row.bars ? row.bars.length : 1) - 1) * (this.lineHeight + 3));
        },
        getHeadingColor(index) {
            return this.headings[index]?.color ?? '#383838';
        },
    }
});
